import styled from "styled-components/macro";
import { typography } from "src/utils/styledComponents/typography";
import { Typography } from "antd";
import { colors } from "src/theme/colors";
import * as themeUi from "src/theme/ui";

/**
 * The dialog's two panes, bounded by the window.
 *
 * Nothing here had a height, so the dialog was as tall as its tallest pane —
 * and the left one states nine account details, a sentence, a currency picker
 * and two more fields, which is around a thousand pixels. On any normal laptop
 * that pushed the dialog past the bottom of the screen and the whole thing
 * scrolled: the Save button, and the form's last two fields, were below the
 * fold of a modal.
 *
 * It is capped at the viewport now, and each pane scrolls its own overflow —
 * so the form's fields and its submit stay together, and the details a
 * customer is transcribing scroll independently of them.
 */
/* Neutral, and as wide as it needs to be — the treatment the onboarding
   wrapper already uses. */
const scrollbar = `
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    border: 2px solid ${colors.white};
    border-radius: 8px;
    background: ${colors.gray[300]};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.gray[400]};
  }
`;

export const Container = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  /* The shared Modal is centered, so the dialog's own height is all that
     stands between it and the edges of the window: at 86vh it left 7vh top
     and bottom, which on a 700px laptop is 49px — and the panes' last field
     was cut by the window rather than by the dialog. */
  max-height: min(80vh, 760px);
`;

/* FINAMAZE's own account details, in the deposit flow's left pane. A tinted
   panel inside a white dialog is the same surface drawn twice, so this is a
   hairline pane now, on the card corner rather than a 15px one. */
export const DetailsContainer = styled.div`
  flex: 0 0 46%;
  min-width: 0;
  /* 32px at the foot, not 24: a scrolling pane needs to end visibly rather
     than with its last field flush against the dialog's edge. */
  padding: 26px 28px 32px;
  border-right: 1px solid ${colors.gray[200]};
  overflow-y: auto;
  overscroll-behavior: contain;

  ${scrollbar}

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const Wrapper = styled.div``;

/* The pane's name, and the control that downloads the same details as a PDF.
   Ink, at the dialog's heading size: it was 1.5em semibold in the accent,
   which is the colour this product reserves for what a reader can act on. */
export const Header = styled.h2`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.015em;
  color: ${colors.gray[900]};
`;

/* A field's name. Every one of the nine in this pane was accent-coloured, so
   the column read as nine links; they are labels, and labels are ink. */
export const Title = styled.span`
  display: block;
  margin-bottom: 5px;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.35;
  color: ${colors.gray[900]};
`;

/* Downloading the details is a secondary action beside the pane's name, so
   it is a quiet white control rather than a filled accent tile. */
export const ImageContainer = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 32px;
  height: 32px;
  border: 1px solid ${colors.gray[200]};
  border-radius: ${themeUi.radii.control}px;
  background-color: ${colors.white};
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease;

  &:hover {
    border-color: ${colors.gray[300]};
    background-color: ${colors.gray[50]};
  }
`;

/* The glyph is a black SVG, so it is recoloured — but to the palette's muted
   ink rather than through a stack of six filter functions hand-tuned to land
   on a blue. */
export const DownloadIcon = styled.img`
  width: 16px;
  height: 16px;
  opacity: 0.55;
`;

export const Body = styled.div``;

/* The sentence in the middle of the pane, at the size a sentence is. */
export const Lead = styled.p`
  margin: 18px 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${colors.gray[500]};
`;

export const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 0;
`;

export const Item = styled.li`
  list-style: none;
`;

/**
 * One of FinaMaze's own account details, to be copied into a bank's wire form.
 *
 * It was `white-space: nowrap` inside a 1px ACCENT border on a 5px corner —
 * so the nine values a customer has to transcribe were nine accent-edged
 * boxes, and the long ones ("FinaMaze Limited - Clients Account", the two
 * bank addresses) were cut off where the box ended. A beneficiary name that
 * cannot be read cannot be checked against the form it is being typed into.
 *
 * It wraps now, on the hairline every other read-only value in this product
 * sits behind, with the value in ink and the copy control at the end of it.
 */
export const Field = styled(Typography.Paragraph)<{
  coppied?: any;
  color?: any;
}>`
  &.ant-typography {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin: 0;
    padding: 10px 12px;
    border: 1px solid
      ${({ coppied }) => (coppied ? colors.success[300] : colors.gray[200])};
    border-radius: ${themeUi.radii.md}px;
    background-color: ${({ coppied }) =>
      coppied ? colors.success[50] : colors.white};
    font-size: 13px;
    line-height: 1.45;
    color: ${colors.gray[900]};
    transition: border-color 0.18s ease, background-color 0.18s ease;
    overflow-wrap: anywhere;
  }

  /* antd puts the copy button in this span; it should not shrink away. */
  .ant-typography-copy {
    flex: none;
    margin: 0;
  }
`;

/**
 * Which currency to wire in — the product's segmented control.
 *
 * It was three tiles on an #e8f1ff ground held at `opacity: 0.27`, with the
 * chosen one brought to full strength under a 4px accent underline. So two of
 * the three currencies a customer might pick were at just over a quarter
 * opacity: not disabled, not unavailable, simply almost invisible — and the
 * one legible tile looked like the only one there was.
 *
 * All three are legible. The chosen one is carried on two channels at once,
 * a white thumb and ink text against muted text on the track, so it survives
 * greyscale; the ones you have not chosen are ordinary readable options.
 */
export const Flags = styled.div`
  display: flex;
  align-items: stretch;
  gap: 3px;
  width: 100%;
  margin-bottom: 14px;
  padding: 3px;
  border-radius: ${themeUi.radii.md}px;
  background-color: ${colors.gray[100]};
`;

export const Flag = styled.div<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 7px 0;
  border-radius: ${themeUi.radii.control - 1}px;
  font-size: 12.5px;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;

  background-color: ${({ active }) => (active ? colors.white : "transparent")};
  color: ${({ active }) => (active ? colors.gray[900] : colors.gray[600])};
  box-shadow: ${({ active }) => (active ? themeUi.shadow.thumb : "none")};

  &:hover {
    color: ${colors.gray[900]};
  }

  ${({ disabled }) =>
    disabled
      ? `
        pointer-events: none;
        color: ${colors.gray[400]};
      `
      : ""}
`;

export const CountryImg = styled.img`
  width: 21px;
  height: 21px;
  border-radius: 99%;
`;

export const InnerFrame = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  padding: 26px 28px 32px;
  overflow-y: auto;
  overscroll-behavior: contain;

  ${scrollbar}
`;

/**
 * The form's column, and its one axis.
 *
 * It was 70% of the dialog with `align-items: center`, holding a
 * FormContainer at 80% of THAT — so the fields occupied 56% of a dialog whose
 * own width was 70% of the window, with the heading and the lead centred
 * above labels that were not. The dialog's width is the measure; everything
 * in it hangs off the same left edge.
 */
export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

export const Actions = styled.div`
  margin-top: 24px;
`;

/* A note under the submit, for what a customer should know before they use
   the account they just added. */
export const SubmitNote = styled.p`
  margin: 10px 0 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: ${colors.gray[500]};
`;

export const HeaderNav = styled.h2`
  /* Ink at the product's heading size. It was 24px of bold Merriweather in
     the accent — a serif heading in a product that has no serif headings, in
     a colour reserved for what a screen is FOR. */
  color: ${colors.gray[900]};
  ${themeUi.text.hero}
  font-size: 18px;

  > a {
    display: none;
  }

  @media (max-width: 1024px) {
    > a {
      display: inline-block;
      margin-left: 1rem;
    }
  }
`;

export const FormContainer = styled.div`
  width: 100%;
  margin-top: 20px;
`;

export const FormTag = styled.span`
  ${typography.CustomText("1rem")}
  font-weight: 400;
  color: #000000;
  margin-bottom: 0.5rem;
`;

/**
 * What the form is for.
 *
 * Three things were wrong with four declarations: `font-family: Arial,
 * Helvetica, sans-serif` hardcoded a face this product does not use anywhere
 * (and had it render in Helvetica beside proxima-nova labels), `font-weight:
 * 0.875rem` is a length where a weight belongs so the browser dropped it, and
 * #7f7f7f is a grey in no palette. Centred, too, over left-aligned fields.
 */
export const BankInfo = styled.div`
  margin-top: 6px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${colors.gray[500]};
`;
